/**
 * @fileoverview Hook Data Layer para formulario de transacción de pago en el Inbox.
 * @module usePaymentTransactionForm
 * @category Hook - Data Layer
 * @since 3.1.0
 * @version 3.1.0
 */
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * @typedef {Object} PaymentTransactionFormValues
 * @property {string} paymentMethod - Método de pago seleccionado
 * @property {string} paymentOther - Método personalizado
 * @property {Array<File>} attachment - Archivos adjuntos
 * @property {string} note - Nota de la transacción
 */

/**
 * Hook Data Layer para gestión del formulario de transacción de pago.
 *
 * - Solo maneja estado y validación del formulario.
 * - No contiene lógica de negocio ni UI.
 * - Optimizaciones: useMemo para schema y opciones.
 *
 * @function usePaymentTransactionForm
 * @returns {Object} { formMethods, paymentOptions, maxChars }
 * @see usePaymentTransactionActions
 * @see usePaymentTransactionUI
 */
export function usePaymentTransactionForm() {
  const { t } = useTranslation('InboxMC');
  const maxChars = 154;

  const validationSchema = useMemo(() =>
    Yup.object().shape({
      paymentMethod: Yup.string().required(t('orders.validation.payment_method_required')),
      paymentOther: Yup.string().when('paymentMethod', {
        is: (val) => val === 'other',
        then: (schema) => schema.required(t('orders.validation.payment_method_required')).trim(),
        otherwise: (schema) => schema.notRequired().trim(),
      }),
    }),
    [t]
  );

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      paymentMethod: '',
      paymentOther: '',
      attachment: [],
      note: ''
    },
    mode: 'onChange'
  });

  const paymentOptions = useMemo(() => ([
    { id: 'bank_transfer', label: t('orders.payment_options.bank_transfer') },
    { id: 'cash', label: t('orders.payment_options.cash') },
    { id: 'cod', label: t('orders.payment_options.cod') },
    { id: 'coupon', label: t('orders.payment_options.coupon') },
    { id: 'card_manual', label: t('orders.payment_options.card_manual') },
    { id: 'check', label: t('orders.payment_options.check') },
    { id: 'other', label: t('orders.payment_options.other') }
  ]), [t]);

  return {
    formMethods,
    paymentOptions,
    maxChars
  };
}
